import { ApiError, NetworkError } from '../../lib/api';
import type { AuthedRequest } from '../auth/AuthProvider';

/** Mirrors GET /api/v1/platform/recording-storage (ADR-036). */
export interface StorageConfig {
  mode: StorageMode;
  s3: StorageS3;
  /** A secret key is stored; the key itself never comes back. */
  secret_set: boolean;
  /** "environment" until the first save on this screen. */
  source: 'database' | 'environment';
  updated_at: string | null;
  updated_by: { id: string; name: string } | null;
}

export type StorageMode = 'local' | 's3';

export interface StorageS3 {
  endpoint: string;
  region: string;
  bucket: string;
  access_key_id: string;
  prefix: string;
  path_style: boolean;
  sse: boolean;
}

export interface StorageBody {
  mode: StorageMode;
  /** secret_access_key "" keeps the stored key. */
  s3: StorageS3 & { secret_access_key: string };
}

export interface StorageCheck {
  ok: boolean;
  step?: CheckStep | undefined;
  reason?: string | undefined;
}

export type CheckStep = 'connect' | 'write' | 'read' | 'delete';

export const storageKey = ['platform', 'recording-storage'] as const;

const path = '/api/v1/platform/recording-storage';

export const getStorage = (req: AuthedRequest) => req<StorageConfig>('GET', path);

/** Tests without saving; slow buckets take up to 30 s. */
export const testStorage = (req: AuthedRequest, body: StorageBody) =>
  req<StorageCheck>('POST', `${path}/test`, body, { timeoutMs: 45_000 });

/** Saves only what passes the same test (409 storage_check_failed otherwise). */
export const saveStorage = (req: AuthedRequest, body: StorageBody) =>
  req<StorageConfig>('PUT', path, body, { timeoutMs: 45_000 });

/** The Backblaze B2 endpoint names its region: s3.<region>.backblazeb2.com. */
export function b2Region(endpoint: string): string | null {
  const m = /^https:\/\/s3\.([a-z0-9-]+)\.backblazeb2\.com\/?$/.exec(endpoint.trim());
  return m?.[1] ?? null;
}

export const stepLabels: Record<CheckStep, string> = {
  connect: 'Conectar ao bucket',
  write: 'Gravar um arquivo de teste',
  read: 'Ler o arquivo de volta',
  delete: 'Apagar o arquivo e as versões dele',
};

export const steps: CheckStep[] = ['connect', 'write', 'read', 'delete'];

const reasons: Record<string, string> = {
  blocked:
    'O endereço foi bloqueado pela política de rede (localhost, rede interna não liberada ou endereço de metadados).',
  unreachable: 'O servidor não respondeu nesse endereço. Confira o endpoint e a saída de internet.',
  timeout: 'O serviço demorou demais para responder.',
  tls: 'O certificado HTTPS do serviço não foi aceito.',
  credentials: 'O ID da chave ou a chave secreta estão errados.',
  denied:
    'Acesso negado. Confira o ID e a chave e se a chave tem acesso a este bucket (no Backblaze, a master key não funciona).',
  bucket_missing: 'O bucket não existe nessa conta ou região.',
  mismatch: 'O arquivo lido não é igual ao gravado.',
  versions_left:
    'A chave apaga o arquivo, mas não as versões antigas. No Backblaze, dê à chave a permissão de apagar arquivos (deleteFiles) ou as gravações não liberam espaço.',
  error: 'O serviço recusou a operação.',
};

export const reasonLabel = (r: string | undefined) =>
  reasons[r ?? ''] ?? 'O serviço recusou a operação.';

const fieldMessages: Record<string, Record<string, string>> = {
  endpoint: {
    required: 'Informe o endereço do serviço.',
    invalid: 'Use só o endereço, sem caminho, como https://s3.us-west-004.backblazeb2.com.',
    https_required: 'Use https://.',
    blocked: 'Endereço bloqueado pela política de rede.',
    too_long: 'Endereço longo demais.',
  },
  bucket: { invalid: 'Use de 3 a 63 letras minúsculas, números, pontos ou hifens.' },
  region: { invalid: 'Use letras minúsculas, números e hifens, como us-west-004.' },
  access_key_id: { required: 'Informe o ID da chave.', invalid: 'ID da chave inválido.' },
  secret_access_key: {
    required: 'Informe a chave secreta.',
    too_long: 'Chave longa demais.',
  },
  prefix: { invalid: 'Use só letras, números, hifens, pontos e barras.' },
};

export interface StorageProblem {
  fields: Record<string, string>;
  form: string | null;
  check: StorageCheck | null;
}

export function toStorageProblem(err: unknown): StorageProblem {
  if (err instanceof ApiError) {
    if (err.status === 422) {
      const fields: Record<string, string> = {};
      for (const [k, code] of Object.entries(err.fields)) {
        fields[k] = fieldMessages[k]?.[code] ?? 'Valor inválido.';
      }
      return { fields, form: null, check: null };
    }
    if (err.status === 409 && err.code === 'storage_check_failed') {
      const e = (err.body as { error?: { step?: CheckStep; reason?: string } } | null)?.error;
      return { fields: {}, form: null, check: { ok: false, step: e?.step, reason: e?.reason } };
    }
    if (err.status === 403)
      return { fields: {}, form: 'Só o Super administrador altera o armazenamento.', check: null };
  }
  if (err instanceof NetworkError)
    return {
      fields: {},
      form: 'Não foi possível contatar o servidor. Tente novamente.',
      check: null,
    };
  return { fields: {}, form: 'Ocorreu um erro inesperado. Tente novamente.', check: null };
}
